/**
 * Amazon Detail Page
 * Analytics for Amazon Marketplace sales via SP-API
 */

import React, { useMemo, useState } from 'react'
import { DollarSign, ShoppingCart, TrendingUp, Package, Target, MapPin, BarChart3, ChevronDown, ChevronUp, RefreshCw, Clock, Truck, AlertCircle, Globe } from 'lucide-react'
import { Link } from 'wouter'
import { useQuery } from '@tanstack/react-query'
import DtcHeader from '../../components/DtcHeader'
import HeroKpiCard from '../../components/HeroKpiCard'
import DtcAIInsightsPanel from '../../components/DtcAIInsightsPanel'
import { DtcShopifySalesChart } from '../../components/DtcShopifySalesChart'
import { CalendarRange, getCalendarDays } from '../../components/DateRangeSelector'
import { formatDtcCurrency, formatDtcNumber } from '../../lib/dtc-data-service'
import { apiClient } from '../../lib/api-client'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts'

// Amazon brand colors
const amazonColor = { 
  primary: '#FF9900', 
  light: '#FFF4E5', 
  medium: '#FFD580',
  dark: '#CC7A00',
  secondary: '#232F3E'
}

// Use the proper Amazon KPI API that queries historical database data
interface TrendDataPoint {
  date: string;
  total_revenue: number;
  total_orders: number;
  total_units: number;
  avg_order_value?: number;
  lw_revenue?: number | null;
  ly_revenue?: number | null;
}

interface AmazonKpiResponse {
  summary: {
    total_revenue: number;
    total_orders: number;
    avg_order_value: number;
    total_units: number;
    total_fees: number;
    net_revenue: number;
    inventory_units: number;
    inventory_value: number;
    // Week-over-Week comparisons
    lw_revenue: number;
    lw_orders: number;
    lw_aov: number;
    lw_units: number;
    lw_revenue_pct: number | null;
    lw_orders_pct: number | null;
    lw_aov_pct: number | null;
    lw_units_pct: number | null;
    // Year-over-Year comparisons
    ly_revenue: number;
    ly_orders: number;
    ly_aov: number;
    ly_units: number;
    ly_revenue_pct: number | null;
    ly_orders_pct: number | null;
    ly_aov_pct: number | null;
    ly_units_pct: number | null;
  };
  trend: TrendDataPoint[];
  top_skus: Array<{
    sku: string;
    product_name: string;
    total_units: number;
    total_revenue: number;
    order_count: number;
  }>;
  date_range: {
    start: string;
    end: string;
    days: number;
  };
}

interface AmazonSyncResponse {
  success: boolean;
  stats: {
    report_lines: number;
    days_processed: number;
    records_updated: number;
    records_inserted: number;
    total_revenue: number;
    total_orders: number;
    elapsed_seconds: string;
  };
}

interface AmazonGeographyState {
  state: string;
  orders: number;
  revenue: number;
  avg_order_value: number;
  cities: number;
  percentage: number;
  units?: number;
}

interface AmazonGeographyResponse {
  top_states: AmazonGeographyState[];
  summary: {
    total_states: number;
    top_10_revenue: number;
  };
  date_range: {
    start: string;
    end: string;
  };
}

interface AmazonFulfillmentChannel {
  channel: string;
  orders: number;
  revenue: number;
  avg_order_value: number;
  units: number;
  percentage: number;
  revenue_percentage: number;
  avg_delivery_days?: number | null;
}

interface AmazonFulfillmentResponse {
  channels: AmazonFulfillmentChannel[];
  summary: {
    total_orders: number;
    total_revenue: number;
  };
  date_range: {
    start: string;
    end: string;
  };
}

interface AmazonProductTrendItem {
  sku: string;
  product_name: string;
  current_revenue: number;
  previous_revenue: number;
  change_percentage: number;
  trend: 'growing' | 'declining' | 'stable' | string;
}

interface AmazonProductTrendsResponse {
  products: AmazonProductTrendItem[];
  period: {
    current: {
      start: string;
      end: string;
    };
    previous: {
      start: string;
      end: string;
    };
  };
}

interface AmazonOrderStatusItem {
  status: string;
  orders: number;
  revenue: number;
  percentage: number;
}

interface AmazonOrderStatusResponse {
  status_breakdown: AmazonOrderStatusItem[];
  summary: {
    total_orders: number;
    cancelled_orders: number;
    cancellation_rate: number;
    is_high_cancellation: boolean;
  };
  trend: Array<{
    week: string;
    total_orders: number;
    cancelled_orders: number;
    cancellation_rate: number;
  }>;
  date_range: {
    start: string;
    end: string;
  };
}

interface AmazonDayOfWeekPattern {
  day_of_week: number;
  day_name: string;
  orders: number;
  revenue: number;
  avg_order_value: number;
  percentage: number;
  is_peak: boolean;
}

interface AmazonShippingServicePattern {
  service_level: string;
  orders: number;
  revenue: number;
  shipping_revenue: number;
  percentage: number;
}

interface AmazonHourPattern {
  hour: number;
  orders: number;
  revenue: number;
  percentage: number;
}

interface AmazonTimePatternsResponse {
  day_of_week: AmazonDayOfWeekPattern[];
  shipping_service: AmazonShippingServicePattern[];
  hour_of_day?: AmazonHourPattern[];
  date_range: {
    start: string;
    end: string;
  };
}

const productLineColors = ['#f59e0b', '#f97316', '#3b82f6', '#10b981', '#8b5cf6'] as const;
const fulfillmentColors = ['#f59e0b', '#f97316', '#3b82f6', '#10b981'] as const;
const statusColors: Record<string, string> = {
  Pending: '#f59e0b',
  Shipped: '#3b82f6',
  Delivered: '#22c55e',
  Cancelled: '#ef4444',
  Returned: '#8b5cf6',
  Other: '#6b7280',
};

function formatCompactCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to load this section right now.';
}

function normalizeFulfillmentChannel(channel: string): string {
  const normalized = channel.toLowerCase();
  if (normalized.includes('fba') || normalized.includes('afn') || normalized.includes('amazon')) {
    return 'FBA';
  }
  if (normalized.includes('mfn') || normalized.includes('merchant')) {
    return 'Merchant Fulfilled';
  }
  return channel || 'Unknown';
}

function normalizeOrderStatus(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized.includes('pending') || normalized.includes('unshipped')) return 'Pending';
  if (normalized.includes('ship') || normalized.includes('transit')) return 'Shipped';
  if (normalized.includes('deliver')) return 'Delivered';
  if (normalized.includes('cancel')) return 'Cancelled';
  if (normalized.includes('return') || normalized.includes('refund')) return 'Returned';
  return 'Other';
}

export default function AmazonDetail() {
  // Date range state - matching Walmart page exactly
  const [calendarRange, setCalendarRange] = useState<CalendarRange>('last7')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [activeCustomDates, setActiveCustomDates] = useState<{start: string, end: string} | null>(null)
  
  // Collapsible sections
  const [showOrderDetails, setShowOrderDetails] = useState(true)
  const [showRevenueTrend, setShowRevenueTrend] = useState(true)
  const [showGeography, setShowGeography] = useState(false)
  const [showFulfillment, setShowFulfillment] = useState(false)
  const [showProductTrends, setShowProductTrends] = useState(false)
  const [showOrderStatus, setShowOrderStatus] = useState(false)
  const [showTimePatterns, setShowTimePatterns] = useState(false)
  
  // Sync state with progress tracking
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [syncProgress, setSyncProgress] = useState<{ step: string; percent: number } | null>(null)
  
  // Calculate date range - matching Walmart/Shopify logic EXACTLY
  let apiOptions: { start_date?: string; end_date?: string } = {}
  let days = 30
  
  if (calendarRange === 'custom' && activeCustomDates) {
    apiOptions.start_date = activeCustomDates.start
    apiOptions.end_date = activeCustomDates.end
    const start = new Date(activeCustomDates.start)
    const end = new Date(activeCustomDates.end)
    days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
  } else if (calendarRange !== 'custom') {
    const end = new Date()
    days = getCalendarDays(calendarRange)
    const start = new Date(end)
    start.setDate(start.getDate() - days + 1)
    apiOptions.start_date = start.toISOString().split('T')[0]
    apiOptions.end_date = end.toISOString().split('T')[0]
  }

  const buildAmazonQueryString = (extraParams: Record<string, string | number> = {}) => {
    const params = new URLSearchParams()
    if (apiOptions.start_date) params.append('start_date', apiOptions.start_date)
    if (apiOptions.end_date) params.append('end_date', apiOptions.end_date)
    params.append('days', days.toString())

    Object.entries(extraParams).forEach(([key, value]) => {
      params.append(key, String(value))
    })

    return params.toString()
  }
  
  // Fetch Amazon data from the PROPER database-backed API (with years of history)
  const { data, isLoading, error, refetch } = useQuery<AmazonKpiResponse>({
    queryKey: ['amazon-kpis', apiOptions.start_date, apiOptions.end_date],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (apiOptions.start_date) params.append('start_date', apiOptions.start_date)
      if (apiOptions.end_date) params.append('end_date', apiOptions.end_date)
      params.append('days', days.toString())
      
      return apiClient.get<AmazonKpiResponse>(`/retailers/amazon/kpis?${params.toString()}`)
    },
    refetchInterval: 1000 * 60 * 5, // Refresh every 5 minutes
  })

  const {
    data: geographyData,
    isLoading: geographyLoading,
    error: geographyError,
  } = useQuery<AmazonGeographyResponse>({
    queryKey: ['amazon-geography', apiOptions.start_date, apiOptions.end_date, days],
    queryFn: async () => {
      const queryString = buildAmazonQueryString({ limit: 10 })
      return apiClient.get<AmazonGeographyResponse>(`/retailers/amazon/geography?${queryString}`)
    },
  })

  const {
    data: fulfillmentData,
    isLoading: fulfillmentLoading,
    error: fulfillmentError,
  } = useQuery<AmazonFulfillmentResponse>({
    queryKey: ['amazon-fulfillment', apiOptions.start_date, apiOptions.end_date, days],
    queryFn: async () => {
      const queryString = buildAmazonQueryString()
      return apiClient.get<AmazonFulfillmentResponse>(`/retailers/amazon/fulfillment?${queryString}`)
    },
  })

  const {
    data: productTrendsData,
    isLoading: productTrendsLoading,
    error: productTrendsError,
  } = useQuery<AmazonProductTrendsResponse>({
    queryKey: ['amazon-product-trends', apiOptions.start_date, apiOptions.end_date, days],
    queryFn: async () => {
      const queryString = buildAmazonQueryString()
      return apiClient.get<AmazonProductTrendsResponse>(`/retailers/amazon/product-trends?${queryString}`)
    },
  })

  const {
    data: orderStatusData,
    isLoading: orderStatusLoading,
    error: orderStatusError,
  } = useQuery<AmazonOrderStatusResponse>({
    queryKey: ['amazon-order-status', apiOptions.start_date, apiOptions.end_date, days],
    queryFn: async () => {
      const queryString = buildAmazonQueryString()
      return apiClient.get<AmazonOrderStatusResponse>(`/retailers/amazon/order-status?${queryString}`)
    },
  })

  const {
    data: timePatternsData,
    isLoading: timePatternsLoading,
    error: timePatternsError,
  } = useQuery<AmazonTimePatternsResponse>({
    queryKey: ['amazon-time-patterns', apiOptions.start_date, apiOptions.end_date, days],
    queryFn: async () => {
      const queryString = buildAmazonQueryString()
      return apiClient.get<AmazonTimePatternsResponse>(`/retailers/amazon/time-patterns?${queryString}`)
    },
  })

  const summary = data?.summary
  const dateRange = data?.date_range
  const trend = data?.trend || []
  const topSkus = data?.top_skus || []

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setActiveCustomDates({ start: customStartDate, end: customEndDate })
    }
  }
  
  // Handle manual sync - uses Reports API for 100% accurate data
  const handleSyncNow = async () => {
    setIsSyncing(true)
    setSyncMessage(null)
    setSyncProgress({ step: 'Starting sync...', percent: 5 })
    
    try {
      // Use Reports API for complete, accurate data (same as Amazon Seller Central)
      const result = await apiClient.get<AmazonSyncResponse>('/amazon/sync-reports?days=7')
      
      // Show success with stats
      const { total_revenue, total_orders, days_processed } = result.stats
      setSyncProgress({ step: 'Complete!', percent: 100 })
      setSyncMessage(`✅ Synced ${days_processed} days: ${total_orders} orders, $${total_revenue.toFixed(2)} revenue`)
      
      // Refresh the KPI data
      await refetch()
      
      // Clear progress after delay
      setTimeout(() => {
        setSyncProgress(null)
        setSyncMessage(null)
      }, 5000)
    } catch (error) {
      setSyncProgress(null)
      setSyncMessage('❌ Sync failed: ' + (error instanceof Error ? error.message : 'Network error'))
    } finally {
      setIsSyncing(false)
    }
  }

  // Use DTC formatting functions for consistency
  const formatCurrency = formatDtcCurrency
  const formatNumber = formatDtcNumber

  // Extract metrics from summary
  const totalOrders = summary?.total_orders || 0
  const totalRevenue = summary?.total_revenue || 0
  const avgOrderValue = summary?.avg_order_value || 0
  const totalUnits = summary?.total_units || 0
  const netRevenue = summary?.net_revenue || 0

  const unitsPerOrder = totalOrders > 0 ? totalUnits / totalOrders : 0
  const geographyRows = (geographyData?.top_states || []).map((state) => {
    const hasUnits = typeof state.units === 'number'
    return {
      ...state,
      display_units: hasUnits ? state.units : Math.round(state.orders * unitsPerOrder),
      units_estimated: !hasUnits,
    }
  })
  const topGeographyState = geographyRows[0]

  const fulfillmentBreakdown = useMemo(() => {
    const grouped = new Map<string, {
      channel: string;
      orders: number;
      revenue: number;
      units: number;
      percentage: number;
      revenue_percentage: number;
      weighted_delivery_days: number;
      delivery_weight: number;
    }>()

    ;(fulfillmentData?.channels || []).forEach((channel) => {
      const channelName = normalizeFulfillmentChannel(channel.channel)
      const existing = grouped.get(channelName) || {
        channel: channelName,
        orders: 0,
        revenue: 0,
        units: 0,
        percentage: 0,
        revenue_percentage: 0,
        weighted_delivery_days: 0,
        delivery_weight: 0,
      }

      existing.orders += channel.orders
      existing.revenue += channel.revenue
      existing.units += channel.units
      existing.percentage += channel.percentage
      existing.revenue_percentage += channel.revenue_percentage

      if (typeof channel.avg_delivery_days === 'number' && Number.isFinite(channel.avg_delivery_days)) {
        existing.weighted_delivery_days += channel.avg_delivery_days * channel.orders
        existing.delivery_weight += channel.orders
      }

      grouped.set(channelName, existing)
    })

    return Array.from(grouped.values())
      .map((channel) => ({
        channel: channel.channel,
        orders: channel.orders,
        revenue: channel.revenue,
        units: channel.units,
        percentage: channel.percentage,
        revenue_percentage: channel.revenue_percentage,
        avg_delivery_days: channel.delivery_weight > 0
          ? channel.weighted_delivery_days / channel.delivery_weight
          : null,
      }))
      .sort((a, b) => b.revenue - a.revenue)
  }, [fulfillmentData?.channels])

  const fbaStats = fulfillmentBreakdown.find((channel) => channel.channel === 'FBA')
  const merchantStats = fulfillmentBreakdown.find((channel) => channel.channel === 'Merchant Fulfilled')
  const hasDeliveryTiming = fulfillmentBreakdown.some((channel) => typeof channel.avg_delivery_days === 'number')
  const emptyFulfillmentChannel = {
    channel: '',
    orders: 0,
    revenue: 0,
    units: 0,
    percentage: 0,
    revenue_percentage: 0,
    avg_delivery_days: null as number | null,
  }
  const fbaDisplay = fbaStats || emptyFulfillmentChannel
  const merchantDisplay = merchantStats || emptyFulfillmentChannel

  const productTrendRows = productTrendsData?.products || []
  const topProductLines = productTrendRows.slice(0, 5)
  const productTrendChartData: Array<Record<string, string | number>> = useMemo(() => {
    const previous: Record<string, string | number> = { period: 'Previous' }
    const current: Record<string, string | number> = { period: 'Current' }

    topProductLines.forEach((product, index) => {
      const dataKey = `series_${index}`
      previous[dataKey] = product.previous_revenue
      current[dataKey] = product.current_revenue
    })

    return [previous, current]
  }, [topProductLines])

  const productMovers = useMemo(
    () => [...productTrendRows].sort((a, b) => b.change_percentage - a.change_percentage),
    [productTrendRows]
  )
  const biggestGainer = productMovers.find((item) => item.change_percentage > 0)
  const biggestDecliner = [...productMovers].reverse().find((item) => item.change_percentage < 0)

  const orderStatusRows = useMemo(() => {
    const requiredStatuses = ['Pending', 'Shipped', 'Delivered', 'Cancelled', 'Returned'] as const
    const grouped = new Map<string, { status: string; orders: number; revenue: number }>()

    ;(orderStatusData?.status_breakdown || []).forEach((statusRow) => {
      const status = normalizeOrderStatus(statusRow.status)
      const existing = grouped.get(status) || { status, orders: 0, revenue: 0 }
      existing.orders += statusRow.orders
      existing.revenue += statusRow.revenue
      grouped.set(status, existing)
    })

    const totalStatusOrders = Array.from(grouped.values()).reduce((sum, status) => sum + status.orders, 0)
    const rows = requiredStatuses.map((status) => {
      const row = grouped.get(status) || { status, orders: 0, revenue: 0 }
      return {
        ...row,
        percentage: totalStatusOrders > 0 ? (row.orders / totalStatusOrders) * 100 : 0,
      }
    })

    const other = grouped.get('Other')
    if (other && other.orders > 0) {
      rows.push({
        ...other,
        percentage: totalStatusOrders > 0 ? (other.orders / totalStatusOrders) * 100 : 0,
      })
    }

    return rows
  }, [orderStatusData?.status_breakdown])

  const dayOfWeekPatterns = [...(timePatternsData?.day_of_week || [])].sort((a, b) => a.day_of_week - b.day_of_week)
  const shippingServicePatterns = (timePatternsData?.shipping_service || []).slice(0, 8)
  const hourOfDayPatterns = [...(timePatternsData?.hour_of_day || [])].sort((a, b) => a.hour - b.hour)

  const peakDay = dayOfWeekPatterns.find((day) => day.is_peak) || dayOfWeekPatterns[0]
  const peakHour = hourOfDayPatterns.length > 0
    ? hourOfDayPatterns.reduce((peak, hour) => (hour.orders > peak.orders ? hour : peak), hourOfDayPatterns[0])
    : null
  const orderStatusPieData = orderStatusRows.filter((status) => status.orders > 0)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Amazon Color Accent Bar */}
      <div 
        className="h-1 w-full"
        style={{ backgroundColor: amazonColor.primary }}
      />
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200 px-6 py-2">
        <div className="max-w-[1400px] mx-auto">
          <nav className="flex items-center text-sm text-gray-600">
            <Link href="/dtc/overview">
              <a className="hover:text-purple-600 transition-colors">DTC Summary</a>
            </Link>
            <span className="mx-2">›</span>
            <span className="font-semibold" style={{ color: amazonColor.primary }}>
              Amazon.com
            </span>
          </nav>
        </div>
      </div>
      
      {/* Header */}
      <DtcHeader
        title="Amazon.com Detail"
        subtitle="Amazon Marketplace Analytics via SP-API"
        onRefresh={refetch}
        isLoading={isLoading}
      />

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-6 space-y-6">
        {/* Date Range Selector */}
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border-2 border-orange-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              📅 Date Range
            </span>
            {dateRange && (
              <span className="text-xs text-gray-600">
                {new Date(dateRange.start).toLocaleDateString()} to {new Date(dateRange.end).toLocaleDateString()}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button 
              onClick={() => setCalendarRange('last7')} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last7' 
                  ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
              }`}
            >
              Last 7 Days
            </button>
            <button 
              onClick={() => setCalendarRange('last30')} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last30' 
                  ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
              }`}
            >
              Last 30 Days
            </button>
            <button 
              onClick={() => setCalendarRange('last90')} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last90' 
                  ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
              }`}
            >
              Last 90 Days
            </button>
            <button 
              onClick={() => setCalendarRange('custom')} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'custom' 
                  ? 'bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50'
              }`}
            >
              📆 Custom Range
            </button>
          </div>

          {/* Custom Date Range Picker */}
          {calendarRange === 'custom' && (
            <div className="mt-4 p-4 bg-white rounded-lg border-2 border-orange-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </div>
              </div>
              <button
                onClick={handleApplyCustomRange}
                disabled={!customStartDate || !customEndDate}
                className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Loading...' : 'View Report'}
              </button>
            </div>
          )}
        </div>

        {/* Amazon Data Sync Section */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className={`w-5 h-5 ${isSyncing ? 'text-orange-500 animate-spin' : 'text-gray-400'}`} />
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Amazon Reports API Sync</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Complete, accurate data matching Seller Central
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Status Indicator */}
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isSyncing ? 'bg-orange-500 animate-pulse' : error ? 'bg-red-500' : 'bg-green-500'}`}></div>
                <span className="text-sm font-medium text-gray-700">
                  {isSyncing ? 'Syncing...' : error ? 'Error' : 'Connected'}
                </span>
              </div>
              
              {/* Sync Now Button */}
              <button
                onClick={handleSyncNow}
                disabled={isSyncing || isLoading}
                className="px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                style={{ backgroundColor: isSyncing ? '#999' : amazonColor.primary }}
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          {syncProgress && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{syncProgress.step}</span>
                <span className="text-sm font-medium text-orange-600">{syncProgress.percent}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ 
                    width: `${syncProgress.percent}%`,
                    backgroundColor: syncProgress.percent === 100 ? '#22c55e' : amazonColor.primary
                  }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Sync Message */}
          {syncMessage && (
            <div className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium ${
              syncMessage.startsWith('✅') 
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {syncMessage}
            </div>
          )}

          {/* Error Message */}
          {error && !syncMessage && (
            <div className="mt-3 px-4 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-800 border border-red-200 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error instanceof Error ? error.message : 'Failed to fetch Amazon data'}
            </div>
          )}
          
          {/* Sync Info */}
          <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
            <Clock className="w-3 h-3" />
            <span>Uses Reports API for 100% accurate data • Syncs automatically twice daily</span>
          </div>
        </div>

        {/* Hero KPI Cards - Row 1 - Matching Walmart exactly */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroKpiCard
            label="Total Revenue"
            value={isLoading ? '...' : formatCurrency(totalRevenue)}
            icon={DollarSign}
            wowDeltaText={summary?.lw_revenue_pct !== null && summary?.lw_revenue_pct !== undefined 
              ? `${summary.lw_revenue_pct >= 0 ? '+' : ''}${summary.lw_revenue_pct.toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.lw_revenue_pct !== undefined && summary?.lw_revenue_pct >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.ly_revenue_pct !== null && summary?.ly_revenue_pct !== undefined 
              ? `${summary.ly_revenue_pct >= 0 ? '+' : ''}${summary.ly_revenue_pct.toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.ly_revenue_pct !== undefined && summary?.ly_revenue_pct >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Total Orders"
            value={isLoading ? '...' : formatNumber(totalOrders)}
            icon={ShoppingCart}
            wowDeltaText={summary?.lw_orders_pct !== null && summary?.lw_orders_pct !== undefined 
              ? `${summary.lw_orders_pct >= 0 ? '+' : ''}${summary.lw_orders_pct.toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.lw_orders_pct !== undefined && summary?.lw_orders_pct >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.ly_orders_pct !== null && summary?.ly_orders_pct !== undefined 
              ? `${summary.ly_orders_pct >= 0 ? '+' : ''}${summary.ly_orders_pct.toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.ly_orders_pct !== undefined && summary?.ly_orders_pct >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Avg Order Value"
            value={isLoading ? '...' : formatCurrency(avgOrderValue)}
            icon={Target}
            wowDeltaText={summary?.lw_aov_pct !== null && summary?.lw_aov_pct !== undefined 
              ? `${summary.lw_aov_pct >= 0 ? '+' : ''}${summary.lw_aov_pct.toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.lw_aov_pct !== undefined && summary?.lw_aov_pct >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.ly_aov_pct !== null && summary?.ly_aov_pct !== undefined 
              ? `${summary.ly_aov_pct >= 0 ? '+' : ''}${summary.ly_aov_pct.toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.ly_aov_pct !== undefined && summary?.ly_aov_pct >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Total Units Sold"
            value={isLoading ? '...' : formatNumber(totalUnits)}
            icon={Package}
            wowDeltaText={summary?.lw_units_pct !== null && summary?.lw_units_pct !== undefined 
              ? `${summary.lw_units_pct >= 0 ? '+' : ''}${summary.lw_units_pct.toFixed(1)}% LW`
              : undefined}
            wowDeltaTone={summary?.lw_units_pct !== undefined && summary?.lw_units_pct >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.ly_units_pct !== null && summary?.ly_units_pct !== undefined 
              ? `${summary.ly_units_pct >= 0 ? '+' : ''}${summary.ly_units_pct.toFixed(1)}% LY`
              : undefined}
            yoyDeltaTone={summary?.ly_units_pct !== undefined && summary?.ly_units_pct >= 0 ? 'success' : 'danger'}
          />
        </div>

        {/* Hero KPI Cards - Row 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroKpiCard
            label="Net Revenue"
            value={isLoading ? '...' : formatCurrency(netRevenue)}
            icon={DollarSign}
            yoyDeltaText="After fees"
            yoyDeltaTone="success"
          />

          <HeroKpiCard
            label="Avg Daily Revenue"
            value={isLoading ? '...' : formatCurrency(totalRevenue / Math.max(days, 1))}
            icon={TrendingUp}
            yoyDeltaText="Per day average"
            yoyDeltaTone="neutral"
          />

          <HeroKpiCard
            label="Avg Units per Order"
            value={isLoading ? '...' : (totalUnits / Math.max(totalOrders, 1)).toFixed(1)}
            icon={Package}
            yoyDeltaText="Units/order"
            yoyDeltaTone="neutral"
          />

          <HeroKpiCard
            label="Active Days"
            value={trend.length.toString() || '0'}
            icon={BarChart3}
            yoyDeltaText="Days with sales"
            yoyDeltaTone="neutral"
          />
        </div>

        {/* AI Insights Panel - Using Amazon platform ID */}
        {apiOptions.start_date && apiOptions.end_date && (
          <DtcAIInsightsPanel
            platformId="40000000-0000-0000-0000-000000000001"
            startDate={apiOptions.start_date}
            endDate={apiOptions.end_date}
            maxInsights={8}
          />
        )}

        {/* Sales Chart - Amazon Orange - Real Data */}
        {trend.length > 0 ? (
          <DtcShopifySalesChart
            data={trend.map(d => ({
              ...d,
              avg_order_value: d.avg_order_value || (d.total_orders > 0 ? d.total_revenue / d.total_orders : 0)
            }))}
            platformColor={amazonColor.primary}
            platformName="Amazon"
            showLyComparison={false}
            showLwComparison={true}
          />
        ) : (
          <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: amazonColor.primary }} />
                  Amazon Sales Trend
                </h3>
                <p className="text-sm text-gray-500">Daily revenue performance</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total Revenue</p>
                <p className="text-xl font-bold" style={{ color: amazonColor.primary }}>
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
            
            <div className="h-[250px] bg-gradient-to-b from-orange-50 to-white rounded-lg border border-orange-200 flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-orange-300" />
                <p className="text-sm font-medium text-gray-600">Loading chart data...</p>
                <p className="text-xs text-gray-500 mt-1">{totalOrders} orders • {formatCurrency(totalRevenue)} revenue</p>
              </div>
            </div>
          </div>
        )}

        {/* Daily Sales Trend Table - Same structure as Walmart */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowRevenueTrend(!showRevenueTrend)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: amazonColor.primary }} />
              Daily Sales Trend ({trend.length > 0 ? trend.length : days} days)
            </h3>
            {showRevenueTrend ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          
          {showRevenueTrend && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {trend.length > 0 ? (
                <>
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Date</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Units</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">AOV</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {trend.slice().reverse().map((point, idx) => {
                          const dateStr = point.date.includes('T') ? point.date : point.date + 'T00:00:00Z'
                          return (
                            <tr key={idx} className="hover:bg-orange-50 transition-colors">
                              <td className="py-3 px-4 text-sm text-gray-900 font-medium">
                                {new Date(dateStr).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric',
                                  timeZone: 'UTC'
                                })}
                              </td>
                              <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">
                                {formatCurrency(point.total_revenue)}
                              </td>
                              <td className="py-3 px-4 text-sm text-right text-gray-700">
                                {formatNumber(point.total_orders)}
                              </td>
                              <td className="py-3 px-4 text-sm text-right text-gray-700">
                                {formatNumber(point.total_units)}
                              </td>
                              <td className="py-3 px-4 text-sm text-right font-medium" style={{ color: amazonColor.primary }}>
                                {formatCurrency(point.avg_order_value)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 text-center">
                    Showing {trend.length} days • Click to collapse
                  </div>
                </>
              ) : (
                <div className="mt-4 py-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                  <Clock className="w-10 h-10 mx-auto mb-3 text-orange-300" />
                  <p className="text-sm font-medium text-gray-600">Loading daily data...</p>
                  <div className="mt-4 grid grid-cols-4 gap-4 max-w-lg mx-auto text-xs">
                    <div className="text-center">
                      <p className="font-semibold text-gray-800">{dateRange?.days || days}</p>
                      <p className="text-gray-500">Days</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-gray-800">{formatNumber(totalOrders)}</p>
                      <p className="text-gray-500">Orders</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-green-600">{formatCurrency(totalRevenue)}</p>
                      <p className="text-gray-500">Revenue</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold" style={{ color: amazonColor.primary }}>{formatCurrency(avgOrderValue)}</p>
                      <p className="text-gray-500">AOV</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Top SKUs - from database */}
        {topSkus.length > 0 && (
          <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => setShowOrderDetails(!showOrderDetails)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5" style={{ color: amazonColor.primary }} />
                Top Products ({topSkus.length} SKUs)
              </h3>
              {showOrderDetails ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {showOrderDetails && (
              <div className="px-6 pb-6 border-t border-gray-200">
                <div className="max-h-96 overflow-y-auto mt-4">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Product</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Units</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topSkus.map((sku, idx) => (
                        <tr key={idx} className="hover:bg-orange-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-900">
                            <div className="font-medium truncate max-w-xs">{sku.product_name}</div>
                            <div className="text-xs text-gray-500">{sku.sku}</div>
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">
                            {formatCurrency(sku.total_revenue)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-700">
                            {formatNumber(sku.total_units)}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-gray-700">
                            {formatNumber(sku.order_count)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Order Insights - Matching Walmart */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" style={{ color: amazonColor.primary }} />
            Order Insights
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg" style={{ backgroundColor: amazonColor.light }}>
              <p className="text-2xl font-bold text-gray-900">
                {formatNumber(totalOrders)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Total Orders</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-900">
                {formatCurrency(avgOrderValue)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Avg Order Value</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-900">
                {formatNumber(totalUnits)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Units Sold</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-900">
                {(totalUnits / Math.max(totalOrders, 1)).toFixed(1)}
              </p>
              <p className="text-xs text-gray-600 mt-1">Units per Order</p>
            </div>
          </div>
        </div>

        {/* Geographic Performance */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowGeography(!showGeography)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Globe className="w-5 h-5" style={{ color: amazonColor.primary }} />
              Geographic Performance
            </h3>
            {showGeography ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showGeography && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {geographyLoading ? (
                <div className="py-10 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: amazonColor.primary }} />
                  Loading geographic performance...
                </div>
              ) : geographyError ? (
                <div className="mt-5 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {getErrorMessage(geographyError)}
                </div>
              ) : geographyRows.length === 0 ? (
                <div className="mt-5 p-8 text-center rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
                  No geographic data for this date range.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-5">
                    <div className="xl:col-span-2 h-[360px] rounded-lg border border-gray-100 p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={geographyRows}
                          layout="vertical"
                          margin={{ top: 8, right: 20, left: 8, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis
                            type="number"
                            tickFormatter={(value) => formatCompactCurrency(Number(value))}
                            stroke="#6b7280"
                            fontSize={12}
                          />
                          <YAxis
                            dataKey="state"
                            type="category"
                            width={70}
                            stroke="#6b7280"
                            fontSize={12}
                          />
                          <Tooltip
                            formatter={(value: number | string) => formatCurrency(Number(value))}
                            cursor={{ fill: '#fff7ed' }}
                          />
                          <Bar dataKey="revenue" fill={amazonColor.primary} radius={[0, 6, 6, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="space-y-3">
                      <div className="p-4 rounded-lg border border-orange-200 bg-orange-50">
                        <p className="text-xs uppercase tracking-wide text-orange-700 font-semibold">Top State</p>
                        <p className="text-lg font-bold text-gray-900 mt-1">{topGeographyState?.state || 'N/A'}</p>
                        <p className="text-sm text-orange-800">{formatCurrency(topGeographyState?.revenue || 0)} revenue</p>
                      </div>
                      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                        <p className="text-xs uppercase tracking-wide text-gray-600 font-semibold">States In Scope</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{formatNumber(geographyData?.summary.total_states || 0)}</p>
                        <p className="text-sm text-gray-600">Top 10 states returned</p>
                      </div>
                      <div className="p-4 rounded-lg border border-green-200 bg-green-50">
                        <p className="text-xs uppercase tracking-wide text-green-700 font-semibold">Top 10 Revenue</p>
                        <p className="text-xl font-bold text-green-900 mt-1">{formatCurrency(geographyData?.summary.top_10_revenue || 0)}</p>
                        <p className="text-sm text-green-700">
                          {formatCurrency(totalRevenue)} total selected revenue
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600 uppercase">State</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Units</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">% Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {geographyRows.map((state) => (
                          <tr key={state.state} className="hover:bg-orange-50 transition-colors">
                            <td className="py-3 px-3 text-sm font-medium text-gray-900">{state.state}</td>
                            <td className="py-3 px-3 text-sm text-right font-semibold text-green-600">
                              {formatCurrency(state.revenue)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {formatNumber(state.orders)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {formatNumber(state.display_units)}
                              {state.units_estimated && (
                                <span className="text-xs text-gray-400 ml-1">*</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {state.percentage.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {geographyRows.some((state) => state.units_estimated) && (
                      <p className="text-xs text-gray-500 mt-2">
                        * Units are estimated when state-level units are not provided by the API.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Fulfillment Breakdown */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowFulfillment(!showFulfillment)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Truck className="w-5 h-5" style={{ color: amazonColor.primary }} />
              Fulfillment Breakdown
            </h3>
            {showFulfillment ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showFulfillment && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {fulfillmentLoading ? (
                <div className="py-10 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: amazonColor.primary }} />
                  Loading fulfillment channels...
                </div>
              ) : fulfillmentError ? (
                <div className="mt-5 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {getErrorMessage(fulfillmentError)}
                </div>
              ) : fulfillmentBreakdown.length === 0 ? (
                <div className="mt-5 p-8 text-center rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
                  No fulfillment channel data for this date range.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                    <div className="p-4 rounded-lg border border-orange-200 bg-orange-50">
                      <p className="text-xs uppercase tracking-wide text-orange-700 font-semibold">FBA</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(fbaDisplay.revenue)}</p>
                      <p className="text-sm text-gray-700 mt-1">{formatNumber(fbaDisplay.orders)} orders • {fbaDisplay.revenue_percentage.toFixed(1)}% revenue</p>
                      <p className="text-xs text-gray-600 mt-2">
                        Avg delivery: {typeof fbaDisplay.avg_delivery_days === 'number' ? `${fbaDisplay.avg_delivery_days.toFixed(1)} days` : 'N/A'}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                      <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Merchant Fulfilled</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(merchantDisplay.revenue)}</p>
                      <p className="text-sm text-gray-700 mt-1">{formatNumber(merchantDisplay.orders)} orders • {merchantDisplay.revenue_percentage.toFixed(1)}% revenue</p>
                      <p className="text-xs text-gray-600 mt-2">
                        Avg delivery: {typeof merchantDisplay.avg_delivery_days === 'number' ? `${merchantDisplay.avg_delivery_days.toFixed(1)} days` : 'N/A'}
                      </p>
                    </div>
                  </div>

                  {!hasDeliveryTiming && (
                    <p className="text-xs text-gray-500 mt-3">
                      Delivery-time metrics are not included in the current fulfillment API payload.
                    </p>
                  )}

                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-5">
                    <div className="xl:col-span-2 h-[320px] rounded-lg border border-gray-100 p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={fulfillmentBreakdown}
                            dataKey="revenue"
                            nameKey="channel"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={110}
                            paddingAngle={2}
                          >
                            {fulfillmentBreakdown.map((channel, index) => (
                              <Cell key={channel.channel} fill={fulfillmentColors[index % fulfillmentColors.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: number | string) => formatCurrency(Number(value))} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                        <p className="text-xs uppercase tracking-wide text-gray-600 font-semibold">Total Orders</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">
                          {formatNumber(fulfillmentData?.summary.total_orders || 0)}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg border border-green-200 bg-green-50">
                        <p className="text-xs uppercase tracking-wide text-green-700 font-semibold">Total Revenue</p>
                        <p className="text-xl font-bold text-green-900 mt-1">
                          {formatCurrency(fulfillmentData?.summary.total_revenue || 0)}
                        </p>
                      </div>
                      <div className="p-4 rounded-lg border border-gray-200 bg-white">
                        <p className="text-xs uppercase tracking-wide text-gray-600 font-semibold">Primary Channel</p>
                        <p className="text-lg font-bold text-gray-900 mt-1">{fulfillmentBreakdown[0]?.channel || 'N/A'}</p>
                        <p className="text-sm text-gray-600 mt-1">{fulfillmentBreakdown[0]?.percentage.toFixed(1) || '0.0'}% of orders</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Channel</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Units</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Revenue Split</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Avg Delivery</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {fulfillmentBreakdown.map((channel) => (
                          <tr key={channel.channel} className="hover:bg-orange-50 transition-colors">
                            <td className="py-3 px-3 text-sm font-medium text-gray-900">{channel.channel}</td>
                            <td className="py-3 px-3 text-sm text-right font-semibold text-green-600">
                              {formatCurrency(channel.revenue)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {formatNumber(channel.orders)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {formatNumber(channel.units)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {channel.revenue_percentage.toFixed(1)}%
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {typeof channel.avg_delivery_days === 'number' ? `${channel.avg_delivery_days.toFixed(1)}d` : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Product Trends */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowProductTrends(!showProductTrends)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-5 h-5" style={{ color: amazonColor.primary }} />
              Product Trends
            </h3>
            {showProductTrends ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showProductTrends && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {productTrendsLoading ? (
                <div className="py-10 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: amazonColor.primary }} />
                  Loading product trends...
                </div>
              ) : productTrendsError ? (
                <div className="mt-5 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {getErrorMessage(productTrendsError)}
                </div>
              ) : productTrendRows.length === 0 ? (
                <div className="mt-5 p-8 text-center rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
                  No product trend data for this date range.
                </div>
              ) : (
                <>
                  <div className="mt-5 p-4 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-600">
                    Current period: <span className="font-semibold text-gray-800">{productTrendsData?.period.current.start}</span> to <span className="font-semibold text-gray-800">{productTrendsData?.period.current.end}</span>
                    <span className="mx-2">•</span>
                    Previous period: <span className="font-semibold text-gray-800">{productTrendsData?.period.previous.start}</span> to <span className="font-semibold text-gray-800">{productTrendsData?.period.previous.end}</span>
                  </div>

                  {topProductLines.length > 0 && (
                    <div className="h-[320px] mt-4 rounded-lg border border-gray-100 p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={productTrendChartData} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="period" stroke="#6b7280" fontSize={12} />
                          <YAxis
                            stroke="#6b7280"
                            fontSize={12}
                            tickFormatter={(value) => formatCompactCurrency(Number(value))}
                          />
                          <Tooltip formatter={(value: number | string) => formatCurrency(Number(value))} />
                          <Legend />
                          {topProductLines.map((product, index) => (
                            <Line
                              key={product.sku}
                              type="monotone"
                              dataKey={`series_${index}`}
                              name={product.sku}
                              stroke={productLineColors[index % productLineColors.length]}
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                    <div className="p-4 rounded-lg border border-green-200 bg-green-50">
                      <p className="text-xs uppercase tracking-wide text-green-700 font-semibold">Biggest Gainer</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {biggestGainer ? `${biggestGainer.product_name} (${biggestGainer.sku})` : 'No positive movers'}
                      </p>
                      <p className="text-sm text-green-800 mt-1">
                        {biggestGainer ? `+${biggestGainer.change_percentage.toFixed(1)}%` : 'N/A'}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg border border-red-200 bg-red-50">
                      <p className="text-xs uppercase tracking-wide text-red-700 font-semibold">Biggest Decliner</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {biggestDecliner ? `${biggestDecliner.product_name} (${biggestDecliner.sku})` : 'No negative movers'}
                      </p>
                      <p className="text-sm text-red-800 mt-1">
                        {biggestDecliner ? `${biggestDecliner.change_percentage.toFixed(1)}%` : 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600 uppercase">SKU</th>
                          <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Product</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Current</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Prior</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Change</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {productTrendRows.slice(0, 10).map((product) => (
                          <tr key={product.sku} className="hover:bg-orange-50 transition-colors">
                            <td className="py-3 px-3 text-sm font-medium text-gray-900">{product.sku}</td>
                            <td className="py-3 px-3 text-sm text-gray-700">
                              {product.product_name.length > 36 ? `${product.product_name.slice(0, 36)}...` : product.product_name}
                            </td>
                            <td className="py-3 px-3 text-sm text-right font-semibold text-green-600">
                              {formatCurrency(product.current_revenue)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {formatCurrency(product.previous_revenue)}
                            </td>
                            <td className={`py-3 px-3 text-sm text-right font-semibold ${
                              product.change_percentage >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {product.change_percentage >= 0 ? '+' : ''}
                              {product.change_percentage.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Order Status */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowOrderStatus(!showOrderStatus)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: amazonColor.primary }} />
              Order Status
            </h3>
            {showOrderStatus ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showOrderStatus && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {orderStatusLoading ? (
                <div className="py-10 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: amazonColor.primary }} />
                  Loading order status...
                </div>
              ) : orderStatusError ? (
                <div className="mt-5 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {getErrorMessage(orderStatusError)}
                </div>
              ) : orderStatusRows.length === 0 ? (
                <div className="mt-5 p-8 text-center rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
                  No order status data for this date range.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-5">
                    <div className="xl:col-span-2 h-[320px] rounded-lg border border-gray-100 p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={orderStatusPieData}
                            dataKey="orders"
                            nameKey="status"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={110}
                            paddingAngle={2}
                          >
                            {orderStatusPieData.map((status) => (
                              <Cell key={status.status} fill={statusColors[status.status] || statusColors.Other} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: number | string) => formatNumber(Number(value))} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-3">
                      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                        <p className="text-xs uppercase tracking-wide text-gray-600 font-semibold">Total Orders</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{formatNumber(orderStatusData?.summary.total_orders || 0)}</p>
                      </div>
                      <div className={`p-4 rounded-lg border ${
                        orderStatusData?.summary.is_high_cancellation ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
                      }`}>
                        <p className={`text-xs uppercase tracking-wide font-semibold ${
                          orderStatusData?.summary.is_high_cancellation ? 'text-red-700' : 'text-green-700'
                        }`}>
                          Cancellation Rate
                        </p>
                        <p className={`text-xl font-bold mt-1 ${
                          orderStatusData?.summary.is_high_cancellation ? 'text-red-900' : 'text-green-900'
                        }`}>
                          {(orderStatusData?.summary.cancellation_rate || 0).toFixed(2)}%
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          {formatNumber(orderStatusData?.summary.cancelled_orders || 0)} cancelled orders
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Share</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {orderStatusRows.map((status) => (
                          <tr key={status.status} className="hover:bg-orange-50 transition-colors">
                            <td className="py-3 px-3 text-sm font-medium text-gray-900 flex items-center gap-2">
                              <span
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: statusColors[status.status] || statusColors.Other }}
                              />
                              {status.status}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {formatNumber(status.orders)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right font-semibold text-green-600">
                              {formatCurrency(status.revenue)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {status.percentage.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Time Patterns */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
          <button
            onClick={() => setShowTimePatterns(!showTimePatterns)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-5 h-5" style={{ color: amazonColor.primary }} />
              Time Patterns
            </h3>
            {showTimePatterns ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showTimePatterns && (
            <div className="px-6 pb-6 border-t border-gray-200">
              {timePatternsLoading ? (
                <div className="py-10 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="w-4 h-4 animate-spin" style={{ color: amazonColor.primary }} />
                  Loading time patterns...
                </div>
              ) : timePatternsError ? (
                <div className="mt-5 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {getErrorMessage(timePatternsError)}
                </div>
              ) : dayOfWeekPatterns.length === 0 ? (
                <div className="mt-5 p-8 text-center rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600">
                  No time-pattern data for this date range.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                    <div className="p-4 rounded-lg border border-orange-200 bg-orange-50">
                      <p className="text-xs uppercase tracking-wide text-orange-700 font-semibold">Peak Day</p>
                      <p className="text-lg font-bold text-gray-900 mt-1">{peakDay?.day_name || 'N/A'}</p>
                      <p className="text-sm text-gray-700 mt-1">{formatNumber(peakDay?.orders || 0)} orders</p>
                    </div>
                    <div className="p-4 rounded-lg border border-blue-200 bg-blue-50">
                      <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold">Peak Hour</p>
                      <p className="text-lg font-bold text-gray-900 mt-1">
                        {peakHour ? `${peakHour.hour}:00` : 'Not available'}
                      </p>
                      <p className="text-sm text-gray-700 mt-1">
                        {peakHour ? `${formatNumber(peakHour.orders)} orders` : 'Hour-of-day data not returned by API'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-5">
                    <div className="h-[320px] rounded-lg border border-gray-100 p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dayOfWeekPatterns} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="day_name" stroke="#6b7280" fontSize={12} />
                          <YAxis stroke="#6b7280" fontSize={12} />
                          <Tooltip formatter={(value: number | string) => formatNumber(Number(value))} />
                          <Bar dataKey="orders" fill={amazonColor.primary} radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="h-[320px] rounded-lg border border-gray-100 p-2">
                      {hourOfDayPatterns.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={hourOfDayPatterns} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                            <XAxis
                              dataKey="hour"
                              stroke="#6b7280"
                              fontSize={12}
                              tickFormatter={(value) => `${value}:00`}
                            />
                            <YAxis stroke="#6b7280" fontSize={12} />
                            <Tooltip
                              formatter={(value: number | string) => formatNumber(Number(value))}
                              labelFormatter={(label) => `${label}:00`}
                            />
                            <Bar dataKey="orders" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={shippingServicePatterns} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                            <XAxis
                              dataKey="service_level"
                              stroke="#6b7280"
                              fontSize={11}
                              tickFormatter={(value) => String(value).slice(0, 14)}
                            />
                            <YAxis stroke="#6b7280" fontSize={12} />
                            <Tooltip formatter={(value: number | string) => formatNumber(Number(value))} />
                            <Bar dataKey="orders" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Day</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                          <th className="text-right py-3 px-3 text-xs font-semibold text-gray-600 uppercase">% Orders</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {dayOfWeekPatterns.map((day) => (
                          <tr key={day.day_of_week} className="hover:bg-orange-50 transition-colors">
                            <td className="py-3 px-3 text-sm font-medium text-gray-900">
                              {day.day_name}
                              {day.is_peak && (
                                <span className="ml-2 text-xs text-orange-600 font-semibold">Peak</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {formatNumber(day.orders)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right font-semibold text-green-600">
                              {formatCurrency(day.revenue)}
                            </td>
                            <td className="py-3 px-3 text-sm text-right text-gray-700">
                              {day.percentage.toFixed(1)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Amazon Marketplace Info */}
        <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-yellow-600 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <Package className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Amazon Marketplace</h2>
              <p className="text-orange-100 text-sm mt-1">Your products on Amazon.com</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Sales Performance */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp className="w-6 h-6 text-yellow-300" />
                <h3 className="font-semibold text-lg">Sales Performance</h3>
              </div>
              <p className="text-sm text-orange-100 mb-3">
                Tracking {totalOrders} orders with {formatCurrency(totalRevenue)} revenue
              </p>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-green-500/20 text-green-300 text-xs font-medium rounded-full border border-green-400/30">
                  ✓ SP-API Connected
                </span>
              </div>
            </div>

            {/* Product Reach */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center gap-3 mb-3">
                <Package className="w-6 h-6 text-pink-300" />
                <h3 className="font-semibold text-lg">Product Reach</h3>
              </div>
              <p className="text-sm text-orange-100 mb-3">
                Your Buttah products available to millions of Amazon shoppers
              </p>
            </div>

            {/* Data Source */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20">
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-6 h-6 text-emerald-300" />
                <h3 className="font-semibold text-lg">SP-API Integration</h3>
              </div>
              <p className="text-sm text-orange-100 mb-3">
                Real-time order data from Amazon Seller Central via SP-API
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
            <p className="text-sm text-orange-100">
              💡 <strong>Live Amazon SP-API</strong> • Order data syncs automatically • Track performance in real-time • Prevent stockouts with demand forecasting
            </p>
          </div>
        </div>

        {/* Demand Forecasting Coming Soon */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
            🚀 Coming Soon: Demand Forecasting
          </h3>
          <p className="text-sm text-gray-600 mb-4">ML-powered predictions to prevent stockouts</p>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-green-700">
              <span>✓</span>
              <span>Historical data collection in progress</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span>⏳</span>
              <span>Training ML model (requires 90 days of data)</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span>⏳</span>
              <span>4-8 week ahead demand predictions</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span>⏳</span>
              <span>Stockout early warning system</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <span>⏳</span>
              <span>Automated replenishment recommendations</span>
            </div>
          </div>
        </div>

        {/* Note about available data */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <p className="text-sm text-orange-900">
            <strong>Data Source:</strong> Amazon SP-API provides real-time order data including revenue, units, and fulfillment status. Full SKU-level details and inventory data coming soon.
          </p>
        </div>
      </div>
    </div>
  )
}
